import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../css/Categories.css'; // Import the CSS file

const Categories = () => {
    const [categoryName, setCategoryName] = useState('');
    const [categories, setCategories] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // States for editing and deleting
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [categoryToEdit, setCategoryToEdit] = useState('');
    const [categoryToDelete, setCategoryToDelete] = useState('');
    const [newCategoryName, setNewCategoryName] = useState('');
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    // New state for loading during edit
    const [isEditing, setIsEditing] = useState(false);

    // Fetch existing categories when component mounts
    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/categories`);
            setCategories(response.data); // Assuming response data is an array of category names
        } catch (error) {
            setErrorMessage('Error fetching categories');
        }
    };

    // Handle Add Category form submission
    const handleAddCategory = async (e) => {
        e.preventDefault();
        setSuccessMessage('');
        setErrorMessage('');

        try {
            const token = localStorage.getItem('token'); // Assuming JWT is stored in localStorage
            const response = await axios.post(
                `${API_BASE_URL}/admin/categories`,
                { category: categoryName },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            setSuccessMessage(response.data.message);
            setCategoryName(''); // Clear the input field after success
            fetchCategories(); // Refresh categories
        } catch (error) {
            if (error.response) {
                console.error('Error response:', error.response);
                setErrorMessage(error.response.data.message || 'Failed to add category');
            } else {
                console.error('Error:', error);
                setErrorMessage('An unexpected error occurred');
            }
        }
    };

    // Handle Edit Category
    const handleEditClick = (category) => {
        setCategoryToEdit(category);
        setNewCategoryName(category);
        setShowEditModal(true);
    };

    const handleEditCategory = async () => {
        setIsEditing(true); // Start loading spinner
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_BASE_URL}/admin/categories/${encodeURIComponent(categoryToEdit)}`,
                { new_name: newCategoryName },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            setSuccessMessage('Category updated successfully');
            setErrorMessage('');
            setShowEditModal(false);
            setCategoryToEdit('');
            fetchCategories(); // Refresh categories
        } catch (error) {
            if (error.response) {
                console.error('Error response:', error.response);
                setErrorMessage(error.response.data.message || 'Failed to update category');
            } else {
                console.error('Error:', error);
                setErrorMessage('An unexpected error occurred');
            }
            setSuccessMessage('');
        } finally {
            setIsEditing(false); // Stop loading spinner
        }
    };

    // Handle Delete Category
    const handleDeleteClick = (category) => {
        setCategoryToDelete(category);
        setShowDeleteModal(true);
    };

    const handleDeleteCategory = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${API_BASE_URL}/admin/categories/${encodeURIComponent(categoryToDelete)}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setSuccessMessage('Category deleted successfully');
            setErrorMessage('');
            setShowDeleteModal(false);
            setCategoryToDelete('');
            fetchCategories(); // Refresh categories
        } catch (error) {
            if (error.response) {
                console.error('Error response:', error.response);
                setErrorMessage(error.response.data.message || 'Failed to delete category');
            } else {
                console.error('Error:', error);
                setErrorMessage('An unexpected error occurred');
            }
            setSuccessMessage('');
        }
    };

    const downloadCategoriesCSV = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/admin/categories/csv`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'categories.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            setErrorMessage('Error downloading Categories CSV');
        }
    };
    
    const downloadCategoriesExcel = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/admin/categories/excel`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'categories.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            setErrorMessage('Error downloading Categories Excel');
        }
    };
    

    return (
        <div className="category-section">
            <h2>Manage Categories (Admin Only)</h2>
            <div className="export-buttons">
    <button onClick={downloadCategoriesCSV}>Download Categories CSV</button>
    <button onClick={downloadCategoriesExcel}>Download Categories Excel</button>
</div>

            {successMessage && <p className="success-message">{successMessage}</p>}
            {errorMessage && <p className="error-message">{errorMessage}</p>}

            <form onSubmit={handleAddCategory} className="category-form">
                <input
                    type="text"
                    placeholder="Category Name"
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    required
                />
                <button type="submit">Add Category</button>
            </form>

            <h3>Available Categories</h3>
            <ul className="category-list">
                {categories.map((category, index) => (
                    <li key={index}>
                        <span className="category-name">{category}</span>
                        <div className="category-actions">
                            <button className="edit-btn" onClick={() => handleEditClick(category)}>
                                Edit
                            </button>
                            <button
                                className="delete-btn"
                                onClick={() => handleDeleteClick(category)}
                            >
                                Delete
                            </button>
                        </div>
                    </li>
                ))}
            </ul>

            {/* Edit Category Modal */}
            {showEditModal && (
                <div className="modal">
                    <div className="modal-content">
                        <span
                            className="close"
                            onClick={() => {
                                setShowEditModal(false);
                                setCategoryToEdit('');
                            }}
                        >
                            &times;
                        </span>
                        <h3>Edit Category</h3>
                        <input
                            type="text"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                        />
                        <button
                            className="save-btn"
                            onClick={handleEditCategory}
                            disabled={isEditing}
                        >
                            {isEditing ? (
                                <div className="spinner"></div>
                            ) : (
                                'Save Changes'
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal">
                    <div className="modal-content">
                        <span
                            className="close"
                            onClick={() => {
                                setShowDeleteModal(false);
                                setCategoryToDelete('');
                            }}
                        >
                            &times;
                        </span>
                        <h3>Confirm Deletion</h3>
                        <p>Are you sure you want to delete the category "{categoryToDelete}"?</p>
                        <div className="modal-buttons">
                            <button className="confirm-delete-btn" onClick={handleDeleteCategory}>
                                Yes, Delete
                            </button>
                            <button
                                className="cancel-btn"
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setCategoryToDelete('');
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Categories;
